import { ProviderType } from 'features/connections'
import { GeocodeBy } from 'features/data-explorer/ui/DataExplorerGeocode/types'
import { QuotaUxTrigger } from 'features/hubspot/types'
import { JobStatus, ProcessingJob, ProcessingJobStatusStatistics } from 'features/jobs/types'
import { MapPrivacy, MapType, SqlParameterType } from 'features/maps/types'
import { User } from 'features/users'
import { WorkflowNodeStatus } from 'features/workflows/enums'
import { MetricEventType } from './api/metrics'
import { EmptyObject } from 'features/common/utils/typeUtils'
import { BuilderMode, WidgetType } from 'features/builder'
import { WorkflowPackageExtensionAnalyticsModel } from '../workflows/types'
import { GetAttributeStatsCategoryOrder } from 'features/builder/api/builder'
import { AgentModelProvider } from 'features/account/api/settingsCartoAI'
export interface DatasetIdProviderMetric {
  datasetId?: string
  connectorType: ProviderType
}

export interface GeocodeMetric {
  geocodingType: GeocodeBy
  connectorType: ProviderType
  connectionId?: string
}

export interface OnboardingItemMetric {
  item?: string
}

export enum EventMetricQuota {
  TotalMaps = 'total_maps',
  TotalApps = 'total_apps',
  MaxPublicMaps = 'max_public_maps',
  MaxEditorUsers = 'max_editor_users',
  MaxViewerUsers = 'max_viewer_users',
  Geocoding = 'geocoding',
  Workflows = 'workflows',
  Usage = 'usage',
  Lds = 'lds'
}
export interface QuotaEventMetric {
  quota: EventMetricQuota
  quota_ux_trigger: QuotaUxTrigger
}
export interface AnalysisCreatedEvent {
  mapId: string
  analysisId: string
  createdAs: 'run' | 'preview' | 'save'
  connectionProvider: ProviderType
  userId: string
  accountId: string
  baseSourceType: MapType
}

export interface EnrichCreatedEventMetricPayload {
  job: ProcessingJob
  statistics: ProcessingJobStatusStatistics
  isCompleted: 'yes' | 'no' | 'cancelled'
}
export interface EnrichCreatedEventMetric {
  createdAs: 'same_table' | 'new_table'
  connectionProvider: ProviderType
  userId: string
  accountId: string
  datasetID: string
  num_variables: number
  process_duration: number
  process_completed: 'yes' | 'no'
}

export type MetricEvent<T> = T & { user: User }

export type OpenBuilderMapMetric = {
  mode: BuilderMode
  mapId?: string
  mapPrivacy?: string
}

export type MapSelectedForAIAgentCreationMetric = {
  mapId: string
}

export type OpenWorkflowMetric = {
  workflowId: string
}

export enum ObjectMetadataContext {
  Instantiation = 'instantiation',
  DataExplorer = 'dataExplorer',
  SqlAnalysis = 'sqlAnalysis',
  TilesetCreation = 'tilesetCreation',
  Enrichment = 'enrichment',
  Lds = 'lds',
  Builder = 'builder'
}

export type TableUsedDetailsMetric = {
  fqn?: string
  geoType?: string
  privacy?: string
  context?: ObjectMetadataContext
  providerId?: string
  connectionId?: string
  client?: string
  objectType?: string // 'table' | 'tileset'
  numRows?: number
  size?: number
  doLocation?: string
}

type ResourceDeletedMetric = {
  fqn: string
  connectionId: string
}

type OptimizeTableLaunchedMetric = {
  fqn: string
  connectionId: string
}

type MapIdPayload = {
  mapId: string
}

type TotalWidgetsByType = {
  [WidgetType.FORMULA]: number
  [WidgetType.CATEGORY]: number
  [WidgetType.HISTOGRAM]: number
  [WidgetType.TABLE]: number
  [WidgetType.TIMESERIES]: number
  [WidgetType.RANGE]: number
}

export type MapUpdatesPublishedMetric = {
  mapId?: string
  context: ObjectMetadataContext
  widgets: TotalWidgetsByType
}

export type HexColorColumnStyledMetric = {
  mapId?: string
}

export type MeasureToolUsedMetric = {
  context: ObjectMetadataContext
  mapId?: string
  measurementUnit: 'kilometers' | 'miles'
}

export type BuilderCustomAggResolutionMetric = {
  mapId?: string
  aggType: 'h3' | 'quadbin'
}

export type BuilderDataExportMetric = {
  mapId?: string
  exportType: 'csv'
  layerCount: number
}

export type BuilderRefreshDataSourceMetric = {
  mapId?: string
  scope: 'all' | 'single_source'
  providerId?: string
}

export type BuilderQueryThisTableMetric = {
  mapId: string
  providerId?: string
}

export type BuilderMaskCreatedMetric = {
  mapId: string
  maskType: string
  role: string
}

export type BuilderURLParameterUsedMetric = {
  mapId: string
  urlParameters: {
    latlon?: true
    layers?: true
    zoom?: true
    bearing?: true
    pitch?: true
    filters?: true
    parameters?: true
    search?: true
    mask?: true
  }
}

export type BuilderReportDownloadMetric = {
  mapId?: string
  context: ObjectMetadataContext
}

export type WorkflowRunMetric = {
  workflowId: string
  components: string[]
  provider: string
  runId: string
}

export type WorkflowExecutionCompleteMetric = {
  workflowId: string
  batchAPIResponse: JobStatus
  components: {
    name: string
    state: WorkflowNodeStatus
    error?: string
  }[]
  provider: string
  runId: string
}

export type WorkflowMapCreatedMetric = {
  workflowId: string
  component?: string
}

export type SQLParameterCreatedMetric = {
  mapId: string
  type: SqlParameterType
  orderBy?: GetAttributeStatsCategoryOrder
}

export type SQLParameterUsedMetric = {
  mapId: string
  mapPrivacy: MapPrivacy
  type: SqlParameterType
}

export type WorkflowUIMetricPayload = {
  workflowId: string
  tenantId?: string
}

export type WorkflowPageSizeMetricPayload = WorkflowUIMetricPayload & {
  pageLength: number
}

export type WorkflowsVariableAddedPayload = {
  workflowId: string
  variableType: string
  isParameter: boolean
}

export type WorkflowsVariableUsedPayload = WorkflowUIMetricPayload & {
  componentType: string
  isParameter: boolean
}

export type DataExplorerCreateActionPayload = {
  connectionId?: string
  providerId?: ProviderType
}

export type HelpSidebarBlockClickPayload = {
  resource: string
}

export type PublishPayload = {
  mapId: string
  type: 'modal' | 'quick_action'
}

export type WorkflowsFileLoadedPayload = WorkflowUIMetricPayload & {
  method: 'drag-and-drop' | 'select'
  tableName: string
}

export type WorkflowsFileDeletedPayload = WorkflowUIMetricPayload & {
  tableName: string
}

export type WorkflowsTemplateExploredPayload = {
  accountId: string
  userId: string
  timestamp: string
}

export type WorkflowTemplateCreatedPayload = {
  accountId: string
  userId: string
  provider: ProviderType
  templateTitle: string
  timestamp: string
}

export type MetricEventProps = {
  eventType: MetricEventType
  extraPayload?: Record<string, unknown>
}

export type LayerTypeChangedPayload = {
  accountId: string
  mapId: string
  type: string
}

export type LayerAggregateByGeometryPayload = {
  accountId: string
  mapId: string
  aggregate: boolean
  geometry: string
}

export type TableWidgetInteractionPayload = {
  accountId: string
  mapId?: string
  type: 'highlight' | 'zoom' | 'search'
}

export type ChangeDatasourcePayload = {
  mapId: string
  type: 'location' | 'connection'
}

export type LayerActionPayload = {
  mapId?: string
}

export type SpatialColumnTypeDefinedPayload = {
  mapId: string
  spatialColumn: string
  spatialType: string
}

export type LegendVisibilityChangedPayload = {
  accountId: string
  mapId?: string
  layerId: string
  state: boolean
}

export type LayerMapListStatusChangedPayload = {
  accountId: string
  mapId?: string
  layerId: string
  state: boolean
}

export type MapCommentPayloadCommon = {
  mapId: string
  authorId: string
  commentId: string
  anchor?: {
    latitude: number
    longitude: number
    zoom?: number
    side?: 'top' | 'bottom' | 'left' | 'right'
  }
}

export type MapCommentPayload = MapCommentPayloadCommon & {
  contentLength: number
  hyperlinksCount: number
  mentions: string[]
}

export type MapCommentUnsplitPayload = {
  mapId: string
  commentIds: string[]
  resolved: boolean
}

export type MapCommentsMarkAllAsReadPayload = {
  mapId: string
  commentIds: string[]
}

export type DataSourceAddedMetric = {
  mapId: string
  type: 'single' | 'multiple'
  createLayer: boolean
}

export type WidgetAggregationUsedPayload = {
  mapId: string
  widgetType: string
  aggregationType: string
}

export type DeleteMapCommentPayload = MapCommentPayloadCommon

export type MapGetCoordinates = {
  mapId: string
}

export type MapCopyCoordinates = {
  mapId: string
}

export type ImportDropFileLoadedPayload = {
  name: string
  type: string
  variant: 'inline' | 'global'
}

export type CrossFilteringWidgetSettingsChangedMetric = {
  mapId: string
  widgetId: string
  widgetType: WidgetType
  crossFilteringEnabled: boolean
  crossFilteringType?: 'single' | 'multiple'
}

export type WidgetFilteringInteractionMetric = {
  mapId: string
  widgetId: string
  widgetType: WidgetType
  crossFilteringType?: 'single' | 'multiple'
}

export type BuilderWidgetOrderByPayload = {
  mapId: string
  widgetId: string
  widgetType: WidgetType
  orderByOption: 'ValuesAscending' | 'ValuesDescending' | 'AlphabeticallyAscending' | 'AlphabeticallyDescending'
}

export type CustomAIModelPayload = {
  AIProvider: AgentModelProvider
  BaseUrl: boolean
  Models: string[]
}

export type AIAgentCreatedPayload = {
  mapId?: string
}

export type AIAgentDeletedPayload = {
  mapId?: string
}

export type AIAgentConfigurationChangedPayload = {
  mapId?: string
  querySources: boolean
  showDetails: boolean
  tools: string[]
  model?: string
}

type AgentConversationMode = 'testing' | 'live'

export type AIAgentConversationStartedPayload = {
  mapId?: string
  conversationId: string
  mode: AgentConversationMode
}

export type AIAgentConversationDeletedPayload = {
  mapId?: string
  conversationId?: string
  mode: AgentConversationMode
}

export type AIAgentResponseStoppedPayload = {
  mapId?: string
  conversationId?: string
  mode: AgentConversationMode
}

export type AIAgentFeedbackPayload = {
  mapId?: string
  conversationId?: string
  mode: AgentConversationMode
  type: 'positive' | 'negative'
}

export type MetricEventPayloadMapping = {
  [MetricEventType.TableUsedDetails]: TableUsedDetailsMetric
  [MetricEventType.ResourceDeleted]: ResourceDeletedMetric
  [MetricEventType.OptimizeTableLaunched]: OptimizeTableLaunchedMetric
  [MetricEventType.OpenBuilderMap]: OpenBuilderMapMetric
  [MetricEventType.BuilderCustomAggResolution]: BuilderCustomAggResolutionMetric
  [MetricEventType.BuilderDataExport]: BuilderDataExportMetric
  [MetricEventType.BuilderQueryThisTable]: BuilderQueryThisTableMetric
  [MetricEventType.BuilderRefreshDataSource]: BuilderRefreshDataSourceMetric
  [MetricEventType.BuilderMaskCreated]: BuilderMaskCreatedMetric
  [MetricEventType.BuilderURLParameterUsed]: BuilderURLParameterUsedMetric
  [MetricEventType.BuilderReportDownload]: BuilderReportDownloadMetric
  [MetricEventType.OpenWorkflow]: OpenWorkflowMetric
  [MetricEventType.WorkflowRun]: WorkflowRunMetric
  [MetricEventType.WorkflowExecutionComplete]: WorkflowExecutionCompleteMetric
  [MetricEventType.WorkflowMapCreated]: WorkflowMapCreatedMetric
  [MetricEventType.SQLParameterCreated]: SQLParameterCreatedMetric
  [MetricEventType.SQLParameterUsed]: SQLParameterUsedMetric
  [MetricEventType.MapDescriptionCreated]: EmptyObject
  [MetricEventType.MapDescriptionUsed]: EmptyObject
  [MetricEventType.MapUpdatesPublished]: MapUpdatesPublishedMetric
  [MetricEventType.MapGetCoordinates]: MapGetCoordinates
  [MetricEventType.MapCopyCoordinates]: MapCopyCoordinates
  [MetricEventType.MeasureToolUsed]: MeasureToolUsedMetric
  [MetricEventType.HexColorColumnStyled]: HexColorColumnStyledMetric
  [MetricEventType.WorkflowsResultCopied]: WorkflowUIMetricPayload
  [MetricEventType.WorkflowsSQLExported]: WorkflowUIMetricPayload
  [MetricEventType.WorkflowsAPIEnabled]: WorkflowUIMetricPayload
  [MetricEventType.WorkflowsAPIDisabled]: WorkflowUIMetricPayload
  [MetricEventType.WorkflowsMcpToolEnabled]: WorkflowUIMetricPayload
  [MetricEventType.WorkflowsMcpToolDisabled]: WorkflowUIMetricPayload
  [MetricEventType.WorkflowsVariableAdded]: WorkflowsVariableAddedPayload
  [MetricEventType.WorkflowsVariableUsed]: WorkflowsVariableUsedPayload
  [MetricEventType.WorkflowsResultPaginationNavigationAction]: WorkflowUIMetricPayload
  [MetricEventType.WorkflowsResultPaginationLengthChanged]: WorkflowPageSizeMetricPayload
  [MetricEventType.WorkflowsListed]: undefined
  [MetricEventType.AiAgentsListed]: undefined
  [MetricEventType.MapSelectedForAIAgentCreation]: MapSelectedForAIAgentCreationMetric
  [MetricEventType.WorkflowsFileLoaded]: WorkflowsFileLoadedPayload
  [MetricEventType.WorkflowsFileDeleted]: WorkflowsFileDeletedPayload
  [MetricEventType.WorkflowsTemplateExplored]: WorkflowsTemplateExploredPayload
  [MetricEventType.WorkflowTemplateCreated]: WorkflowTemplateCreatedPayload
  [MetricEventType.DataExplorerMapCreated]: DataExplorerCreateActionPayload
  [MetricEventType.DataExplorerWorkflowCreated]: DataExplorerCreateActionPayload
  [MetricEventType.OpenApplications]: EmptyObject
  [MetricEventType.InviteUsersMainMenuClicked]: EmptyObject
  [MetricEventType.HelpSidebarBlockClick]: HelpSidebarBlockClickPayload
  [MetricEventType.CopyMapLink]: PublishPayload
  [MetricEventType.CopyEmbedCode]: MapIdPayload
  [MetricEventType.CopyMapId]: MapIdPayload
  [MetricEventType.CopyMapToken]: MapIdPayload
  [MetricEventType.MapPublishUpdate]: PublishPayload
  [MetricEventType.MapPreviewMode]: MapIdPayload
  [MetricEventType.MapEditMode]: MapIdPayload
  [MetricEventType.LayerTypeChanged]: LayerTypeChangedPayload
  [MetricEventType.LayerAggregateByGeometry]: LayerAggregateByGeometryPayload
  [MetricEventType.WorkflowsExtensionInstalled]: WorkflowPackageExtensionAnalyticsModel
  [MetricEventType.WorkflowsExtensionUninstalled]: WorkflowPackageExtensionAnalyticsModel
  [MetricEventType.WorkflowsExtensionFailed]: WorkflowPackageExtensionAnalyticsModel
  [MetricEventType.ZoomToLayer]: LayerActionPayload
  [MetricEventType.ShowOnlyThisLayer]: LayerActionPayload
  [MetricEventType.SpatialColumnTypeDefined]: SpatialColumnTypeDefinedPayload
  [MetricEventType.TableWidgetInteraction]: TableWidgetInteractionPayload
  [MetricEventType.ChangeDatasource]: ChangeDatasourcePayload
  [MetricEventType.UserLogout]: EmptyObject
  [MetricEventType.LegendVisibilityChanged]: LegendVisibilityChangedPayload
  [MetricEventType.LayerMapListStatusChanged]: LayerMapListStatusChangedPayload
  [MetricEventType.BuilderCommentCreated]: MapCommentPayload
  [MetricEventType.BuilderCommentReplyCreated]: MapCommentPayload
  [MetricEventType.BuilderCommentResolved]: MapCommentPayload
  [MetricEventType.BuilderCommentReopened]: MapCommentPayload
  [MetricEventType.BuilderCommentEdited]: MapCommentPayload
  [MetricEventType.BuilderCommentReplyEdited]: MapCommentPayload
  [MetricEventType.BuilderCommentDeleted]: DeleteMapCommentPayload
  [MetricEventType.BuilderCommentReplyDeleted]: DeleteMapCommentPayload
  [MetricEventType.BuilderCommentsMarkAllAsRead]: MapCommentsMarkAllAsReadPayload
  [MetricEventType.BuilderCommentsUnsplit]: MapCommentUnsplitPayload
  [MetricEventType.DataSourceAdded]: DataSourceAddedMetric
  [MetricEventType.ImportDropFileLoaded]: ImportDropFileLoadedPayload
  [MetricEventType.CrossFilteringWidgetSettingsChanged]: CrossFilteringWidgetSettingsChangedMetric
  [MetricEventType.WidgetFilteringInteraction]: WidgetFilteringInteractionMetric
  [MetricEventType.WidgetAggregationUsed]: WidgetAggregationUsedPayload
  [MetricEventType.OpenDE]: EmptyObject
  [MetricEventType.BuilderWidgetOrderBy]: BuilderWidgetOrderByPayload
  [MetricEventType.SettingsCartoAIEnabled]: EmptyObject
  [MetricEventType.SettingsCartoAIDisabled]: EmptyObject
  [MetricEventType.CustomAIModelAdded]: CustomAIModelPayload
  [MetricEventType.CustomAIModelUpdated]: CustomAIModelPayload
  [MetricEventType.CustomAIModelRemoved]: CustomAIModelPayload
  [MetricEventType.AIAgentCreated]: AIAgentCreatedPayload
  [MetricEventType.AIAgentDeleted]: AIAgentDeletedPayload
  [MetricEventType.AIAgentConfigurationChanged]: AIAgentConfigurationChangedPayload
  [MetricEventType.AIAgentConversationStarted]: AIAgentConversationStartedPayload
  [MetricEventType.AIAgentConversationDeleted]: AIAgentConversationDeletedPayload
  [MetricEventType.AIAgentResponseStopped]: AIAgentResponseStoppedPayload
  [MetricEventType.AIAgentFeedback]: AIAgentFeedbackPayload
  [MetricEventType.SearchConnections]: EmptyObject
  [MetricEventType.ConnectionBookmarkFilterApplied]: EmptyObject
}
